import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../scripts/AuthContext";
import { searchGifs, trendingGifs } from "@/lib/dmApi";
import { searchSocialGifs, trendingSocialGifs } from "@/lib/socialMedia";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const PANEL_WIDTH = 320;
const PANEL_MAX_HEIGHT = 380;
const DEBOUNCE_MS = 300;

function computePanelStyle(anchorRect) {
  if (!anchorRect) return { top: 0, left: 0 };
  const gap = 4;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchorRect.left;
  if (left + PANEL_WIDTH > vw - 8) {
    left = Math.max(8, anchorRect.right - PANEL_WIDTH);
  }
  let top = anchorRect.bottom + gap;
  const spaceBelow = vh - anchorRect.bottom - gap;
  if (spaceBelow < 200 && anchorRect.top > spaceBelow) {
    top = Math.max(8, anchorRect.top - PANEL_MAX_HEIGHT - gap);
  }
  return {
    top,
    left,
    width: PANEL_WIDTH,
    maxHeight: Math.min(PANEL_MAX_HEIGHT, Math.max(200, vh - top - 8)),
  };
}

/**
 * Giphy search/trending picker (proxied via API).
 * onSelect(gifDto) — same shape as server DTO / gif attachment.
 */
export default function GifPickerButton({
  orgId,
  social = false,
  onSelect,
  disabled = false,
  className,
}) {
  const { accessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setPanelStyle(null);
      return;
    }
    const update = () => {
      setPanelStyle(computePanelStyle(buttonRef.current.getBoundingClientRect()));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        panelRef.current?.contains(e.target) ||
        buttonRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !accessToken || (!social && !orgId)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const list = social
          ? debouncedQ
            ? await searchSocialGifs(accessToken, debouncedQ, { limit: 24 })
            : await trendingSocialGifs(accessToken, { limit: 24 })
          : debouncedQ
            ? await searchGifs(accessToken, orgId, debouncedQ, { limit: 24 })
            : await trendingGifs(accessToken, orgId, { limit: 24 });
        if (!cancelled) setGifs(list);
      } catch (err) {
        if (!cancelled) {
          setGifs([]);
          setError(
            err.response?.data?.message ||
              err.message ||
              "Could not load GIFs"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, orgId, social, debouncedQ]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQ("");
      setGifs([]);
      setError(null);
    }
  }, [open]);

  const pick = (gif) => {
    onSelect?.({
      type: "gif",
      id: gif.id,
      title: gif.title,
      url: gif.url,
      preview_url: gif.preview_url,
      mp4_url: gif.mp4_url,
      width: gif.width,
      height: gif.height,
    });
    setOpen(false);
  };

  return (
    <>
      <Button
        ref={buttonRef}
        type="button"
        variant="ghost"
        size="icon"
        className={cn("h-8 w-8 shrink-0", className)}
        onClick={() => setOpen((v) => !v)}
        title="GIF"
        disabled={disabled}
        aria-label="Insert GIF"
        aria-expanded={open}
      >
        <span className="text-[10px] font-bold leading-none tracking-wide">
          GIF
        </span>
      </Button>
      {open && panelStyle
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed z-[200] flex flex-col rounded-lg border border-border bg-popover text-popover-foreground shadow-lg overflow-hidden"
              style={panelStyle}
            >
              <div className="p-2 border-b border-border shrink-0 space-y-1">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search Giphy…"
                  className="h-8 text-sm"
                  autoFocus
                />
                <p className="text-[10px] text-muted-foreground px-0.5">
                  {debouncedQ ? "Search results" : "Trending"} · Powered by Giphy
                </p>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-2">
                {loading ? (
                  <div className="flex items-center justify-center py-10 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : error ? (
                  <p className="text-xs text-destructive text-center py-6 px-2">
                    {typeof error === "string" ? error : "Could not load GIFs"}
                  </p>
                ) : gifs.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    No GIFs found
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5">
                    {gifs.map((gif) => (
                      <button
                        key={gif.id}
                        type="button"
                        className="relative aspect-square overflow-hidden rounded-md bg-muted hover:ring-2 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => pick(gif)}
                        title={gif.title}
                      >
                        <img
                          src={gif.preview_url || gif.url}
                          alt={gif.title || "GIF"}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
